(() => {
  if (customElements.get('hx-gallery-grid')) return;
  class HexagonGalleryGrid extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.secondary = this.querySelector('.hx-gallery-grid-secondary');
      this.lightbox = this.querySelector('.hx-product-lightbox');
      this.productGrid = this.closest('.hx-product-grid');
      this.purchase = this.productGrid?.querySelector('.hx-product-purchase');
      this.mobile = matchMedia('(max-width: 749px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.setupMobileCarousel();
      this.updateLayout = () => {
        if (!this.secondary || !this.productGrid) return;
        this.syncMobileCarousel();
        if (this.mobile.matches) this.productGrid.append(this.secondary);
        else this.insertBefore(this.secondary, this.lightbox);
        this.updateDock();
      };
      this.updateDock = () => {
        if (!this.purchase) return;
        this.purchase.classList.toggle('hx-gallery-purchase', !this.mobile.matches);
        if (this.mobile.matches) {
          this.purchase.style.removeProperty('--hx-dock-top');
          this.purchase.style.removeProperty('--hx-dock-height');
          return;
        }
        const form = this.purchase.querySelector('.hx-product-form');
        if (!form) return;
        const panelRect = this.purchase.getBoundingClientRect();
        const formRect = form.getBoundingClientRect();
        const offset = formRect.top - panelRect.top + this.purchase.scrollTop;
        const buyRect = form.querySelector('.hx-product-buy-row').getBoundingClientRect();
        const clearance = Math.min(110, Math.max(24, innerHeight - (buyRect.bottom - formRect.top) - 24));
        const top = Math.round(clearance - offset);
        const height = Math.round(innerHeight - top - 18);
        this.purchase.style.setProperty('--hx-dock-top', top + 'px');
        this.purchase.style.setProperty('--hx-dock-height', height + 'px');
      };
      window.addEventListener('resize', this.updateDock, { signal: this.controller.signal });
      if ('ResizeObserver' in window && this.purchase) {
        this.purchaseObserver = new ResizeObserver(() => this.updateDock());
        this.purchaseObserver.observe(this.purchase);
        this.purchase.querySelectorAll('.hx-product-form,.hx-product-intro').forEach(element => this.purchaseObserver.observe(element));
      }
      if ('ResizeObserver' in window) {
        this.ribbonObserver = new ResizeObserver(entries => {
          entries.forEach(({ target }) => {
            const duration = Math.max(20, target.getBoundingClientRect().width / 24);
            target.parentElement.style.setProperty('--hx-ribbon-duration', duration.toFixed(2) + 's');
          });
        });
        this.querySelectorAll('.hx-gallery-ribbon-group:first-child').forEach(group => this.ribbonObserver.observe(group));
      }
      this.mobile.addEventListener('change', this.updateLayout, { signal: this.controller.signal });
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => entry.target.classList.toggle('is-visible', entry.isIntersecting && !document.hidden));
        }, { threshold: .1 });
        this.querySelectorAll('.hx-gallery-grid-card').forEach(card => this.observer.observe(card));
      }
      document.addEventListener('visibilitychange', () => {
        const cards = [...this.querySelectorAll('.hx-gallery-grid-card'), ...this.secondary.querySelectorAll('.hx-gallery-grid-card')];
        cards.forEach(card => {
          const rect = card.getBoundingClientRect();
          card.classList.toggle('is-visible', !document.hidden && rect.bottom > 0 && rect.top < innerHeight);
        });
      }, { signal: this.controller.signal });
      this.updateLayout();
    }
    setupMobileCarousel() {
      this.mediaCards = [...this.querySelectorAll('.hx-gallery-grid-card')];
      this.hero = this.querySelector('.hx-gallery-grid-hero');
      this.tiles = this.querySelector('.hx-gallery-grid-tiles');
      if (!this.hero || !this.tiles || this.mediaCards.length < 2) return;
      const signal = this.controller.signal;
      this.mobileTrack = document.createElement('div');
      this.mobileTrack.className = 'hx-gallery-mobile-track';
      this.mobileTrack.hidden = true;
      this.mobileTrack.tabIndex = 0;
      this.mobileTrack.setAttribute('role', 'region');
      this.mobileTrack.setAttribute('aria-label', this.getAttribute('aria-label') || '');
      this.insertBefore(this.mobileTrack, this.hero);
      this.mobileNavigation = document.createElement('div');
      this.mobileNavigation.className = 'hx-gallery-mobile-navigation';
      this.mobileNavigation.hidden = true;
      this.mobileDots = this.mediaCards.map((card, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        const alt = card.querySelector('img')?.alt;
        button.setAttribute('aria-label', `${index + 1} / ${this.mediaCards.length}${alt ? ' — ' + alt : ''}`);
        button.setAttribute('aria-current', String(index === 0));
        button.addEventListener('click', () => this.scrollToCard(index), { signal });
        this.mobileNavigation.append(button);
        return button;
      });
      this.mobileTrack.after(this.mobileNavigation);
      this.mobileIndex = 0;
      this.mobileTrack.addEventListener('scroll', () => {
        if (this.carouselFrame) return;
        this.carouselFrame = requestAnimationFrame(() => {
          this.carouselFrame = null;
          if (!this.mobile.matches) return;
          const left = this.mobileTrack.getBoundingClientRect().left;
          let closest = 0;
          this.mediaCards.forEach((card, index) => {
            if (Math.abs(card.getBoundingClientRect().left - left) < Math.abs(this.mediaCards[closest].getBoundingClientRect().left - left)) closest = index;
          });
          this.mobileIndex = closest;
          this.mobileDots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === closest)));
        });
      }, { passive: true, signal });
      this.mobileTrack.addEventListener('keydown', event => {
        let index = this.mobileIndex;
        if (event.key === 'ArrowRight') index++;
        else if (event.key === 'ArrowLeft') index--;
        else if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = this.mediaCards.length - 1;
        else return;
        event.preventDefault();
        this.scrollToCard(Math.max(0, Math.min(this.mediaCards.length - 1, index)));
      }, { signal });
    }
    scrollToCard(index) {
      const card = this.mediaCards[index];
      if (!card || !this.mobileTrack) return;
      this.mobileTrack.scrollTo({ left: card.offsetLeft, behavior: this.reduced.matches ? 'instant' : 'smooth' });
    }
    syncMobileCarousel() {
      if (!this.mobileTrack) return;
      if (this.mobile.matches) {
        this.mobileTrack.hidden = false;
        this.mobileNavigation.hidden = false;
        this.mediaCards.forEach(card => this.mobileTrack.append(card));
      } else {
        this.insertBefore(this.hero, this.mobileTrack);
        this.mediaCards.slice(1).forEach(card => this.tiles.append(card));
        this.mobileTrack.hidden = true;
        this.mobileNavigation.hidden = true;
      }
    }
    disconnectedCallback() {
      if (this.carouselFrame) cancelAnimationFrame(this.carouselFrame);
      if (this.mobileTrack) {
        this.insertBefore(this.hero, this.mobileTrack);
        this.mediaCards.slice(1).forEach(card => this.tiles.append(card));
        this.mobileTrack.remove();
        this.mobileNavigation.remove();
      }
      this.observer?.disconnect();
      this.purchaseObserver?.disconnect();
      this.ribbonObserver?.disconnect();
      this.purchase?.classList.remove('hx-gallery-purchase');
      this.purchase?.style.removeProperty('--hx-dock-top');
      this.purchase?.style.removeProperty('--hx-dock-height');
      this.controller?.abort();
      this.controller = null;
      if (this.secondary && !this.contains(this.secondary)) this.insertBefore(this.secondary, this.lightbox);
    }
  }
  customElements.define('hx-gallery-grid', HexagonGalleryGrid);
})();

